import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Space,
  Typography,
  Divider,
  Spin,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { updateInvoice } from "../../services/invoiceService";
import { UNIT_CODE_OPTIONS } from "../../constants/invoiceConstants";
import { formatPrice } from "../../utils/formatters";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Text } = Typography;

const UpdateInvoiceModal = ({ visible, invoice, onCancel, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const watchedItems = Form.useWatch("items", form);

  useEffect(() => {
    if (visible && invoice) {
      form.setFieldsValue({
        dueDate: invoice.dueDate ? dayjs(invoice.dueDate) : null,
        notes: invoice.notes,
        items: (invoice.items || []).map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitCode: item.unitCode || "ADET",
          unitPrice: item.unitPrice,
          taxRate: item.taxRate || 0,
        })),
      });
    }
  }, [visible, invoice, form]);

  const calculateTotals = () => {
    const items = watchedItems || [];
    let subtotal = 0;
    let totalTax = 0;

    items.forEach((item) => {
      if (item) {
        const quantity = item.quantity || 0;
        const unitPrice = item.unitPrice || 0;
        const taxRate = item.taxRate || 0;
        const lineTotal = quantity * unitPrice;
        const lineTax = lineTotal * (taxRate / 100);
        subtotal += lineTotal;
        totalTax += lineTax;
      }
    });

    return { subtotal, totalTax, total: subtotal + totalTax };
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const invoiceData = {
        dueDate: values.dueDate ? values.dueDate.format("YYYY-MM-DD") : null,
        notes: values.notes,
        items: (values.items || []).map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitCode: item.unitCode || "ADET",
          unitPrice: item.unitPrice,
          taxRate: item.taxRate || 0,
        })),
      };

      await updateInvoice(invoice.id, invoiceData);
      toast.success("Fatura başarıyla güncellendi");
      onSuccess();
    } catch (error) {
      if (!error.errorFields) {
        console.error("Error updating invoice:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <Modal
      title="Fatura Düzenle"
      open={visible}
      onCancel={onCancel}
      maskClosable={false}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          İptal
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          Güncelle
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item name="dueDate" label="Vade Tarihi">
            <DatePicker
              format="DD.MM.YYYY"
              placeholder="Vade tarihi seçin"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Divider>Fatura Kalemleri</Divider>

          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space
                    key={key}
                    style={{ display: "flex", marginBottom: 8, flexWrap: "wrap", gap: 8 }}
                    align="start"
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "description"]}
                      rules={[{ required: true, message: "Açıklama gerekli" }]}
                      style={{ minWidth: 200, flex: 2 }}
                    >
                      <Input placeholder="Açıklama" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "quantity"]}
                      rules={[{ required: true, message: "Miktar gerekli" }]}
                      style={{ width: 110 }}
                    >
                      <InputNumber min={0.000001} step={1} placeholder="Miktar" style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "unitCode"]}
                      style={{ width: 110 }}
                    >
                      <Select
                        options={UNIT_CODE_OPTIONS}
                        placeholder="Birim"
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "unitPrice"]}
                      rules={[{ required: true, message: "Fiyat gerekli" }]}
                      style={{ width: 130 }}
                    >
                      <InputNumber
                        min={0}
                        step={0.01}
                        placeholder="Birim Fiyat"
                        style={{ width: "100%" }}
                        addonAfter="₺"
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "taxRate"]}
                      style={{ width: 100 }}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        placeholder="KDV %"
                        style={{ width: "100%" }}
                        addonAfter="%"
                      />
                    </Form.Item>
                    {fields.length > 1 && (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    )}
                  </Space>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Kalem Ekle
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <div style={{ textAlign: "right", marginBottom: 16 }}>
            <Space direction="vertical" size={4}>
              <Text>Ara Toplam: {formatPrice(totals.subtotal)}</Text>
              <Text>KDV: {formatPrice(totals.totalTax)}</Text>
              <Text strong style={{ fontSize: 16 }}>
                Genel Toplam: {formatPrice(totals.total)}
              </Text>
            </Space>
          </div>

          <Form.Item name="notes" label="Notlar">
            <TextArea rows={3} placeholder="Fatura notları" />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default UpdateInvoiceModal;
